import React from 'react';
import PropTypes from 'prop-types';
import bowser from 'yola-bowser';
import { designSystem, Modal, Panel, PanelGroup } from '@yola/ws-ui';
import Breadcrumbs from 'src/js/modules/common/components/breadcrumbs';
import constants from '../constants';
import FontPreviewOption from './font-preview-option';
import previewGifs from '../constants/preview-gifs';

const {
  DialogHeader,
  Box,
  Stack,
  DialogButtonGroup,
  DialogButton,
  Paragraph,
  Heading,
  DialogButtonGroupSeparator,
  ControlGroup,
  SearchableSelect,
  Divider,
} = designSystem;

const { DEFAULT_DIALOG_HEIGHT, DEFAULT_DIALOG_WIDTH, DEFAULT_DIALOG_MIN_HEIGHT } = constants;

const ElementTypeOption = ({ children, observer }) => (
  <FontPreviewOption observer={observer}>{children}</FontPreviewOption>
);

const AddFontPairDialog = ({
  offsetX,
  offsetY,
  captions,
  fontsOptions,
  defaultOptions,
  fontObserver,
  onCancel,
  onSubmit,
  onPrimaryFontChange,
  onSecondaryFontChange,
  onDragEnd,
}) => (
  <Modal
    draggable
    fullscreen={bowser.mobile}
    width={DEFAULT_DIALOG_WIDTH}
    minWidth={DEFAULT_DIALOG_WIDTH}
    maxWidth={DEFAULT_DIALOG_WIDTH}
    height={DEFAULT_DIALOG_HEIGHT}
    minHeight={DEFAULT_DIALOG_MIN_HEIGHT}
    offsetX={offsetX}
    offsetY={offsetY}
    dragHandleSelector=".ws-drag-trigger"
    dragCancelSelector=".ws-ui-dialog-header-controls"
    overlay="none"
    handleCancel={onCancel}
    handleSubmit={onSubmit}
    onOverlayClick={onSubmit}
    onDragEnd={onDragEnd}
    className="ws-design-fonts-dialog"
    resizeIcon="ns-resize"
  >
    <PanelGroup height="100%">
      <Panel
        align="middle"
        corners="squared"
        className="ws-drag-trigger"
        style={{ flexShrink: '0' }}
      >
        <DialogHeader paddingBottom="spacing-s">
          <Box>
            <Box paddingBottom="spacing-2-xs">
              <Breadcrumbs breadcrumbs={captions.breadcrumbs} />
            </Box>
            <Box>
              <Stack>
                <Heading type="heading-3">{captions.title}</Heading>
                <Paragraph>{captions.description}</Paragraph>
              </Stack>
            </Box>
          </Box>
        </DialogHeader>
      </Panel>
      <Panel
        height="100%"
        scrollable
        style={{ borderBottom: 'none' }}
        className="ws-design-fonts-dialog__content-wrapper"
      >
        <div className="ws-design-fonts-dialog__content">
          <Box
            paddingTop="spacing-xs"
            paddingBottom="spacing-s"
            paddingLeft="spacing-m"
            paddingRight="spacing-m"
          >
            <ControlGroup
              title={captions.primaryFontTitle}
              popoverProps={{
                content: captions.primaryFontPopover,
                imgSrc: previewGifs.PRIMARY_FONT_GIF,
              }}
            >
              <SearchableSelect
                noResultsText={captions.noResultsText}
                options={fontsOptions}
                components={{
                  Option: (props) => <ElementTypeOption {...props} observer={fontObserver} />,
                }}
                defaultValue={defaultOptions.primaryFont}
                onChange={onPrimaryFontChange}
              />
            </ControlGroup>
          </Box>

          <Divider appearance="light-grey" />

          <Box
            paddingTop="spacing-xs"
            paddingBottom="spacing-s"
            paddingLeft="spacing-m"
            paddingRight="spacing-m"
          >
            <ControlGroup
              title={captions.secondaryFontTitle}
              popoverProps={{
                content: captions.secondaryFontPopover,
                imgSrc: previewGifs.SECONDARY_FONT_GIF,
              }}
            >
              <SearchableSelect
                noResultsText={captions.noResultsText}
                options={fontsOptions}
                components={{
                  Option: (props) => <ElementTypeOption {...props} observer={fontObserver} />,
                }}
                defaultValue={defaultOptions.secondaryFont}
                onChange={onSecondaryFontChange}
              />
            </ControlGroup>
          </Box>
        </div>
      </Panel>
      <Panel corners="squared" style={{ flexShrink: '0' }}>
        <DialogButtonGroup>
          <DialogButton onClick={onSubmit}>{captions.submitButton}</DialogButton>
          <DialogButtonGroupSeparator />
          <DialogButton onClick={onCancel}>{captions.cancelButton}</DialogButton>
        </DialogButtonGroup>
      </Panel>
    </PanelGroup>
  </Modal>
);

ElementTypeOption.propTypes = {
  children: PropTypes.node.isRequired,
  observer: PropTypes.shape({
    observe: PropTypes.func,
  }).isRequired,
};

AddFontPairDialog.propTypes = {
  offsetX: PropTypes.number.isRequired,
  offsetY: PropTypes.number.isRequired,
  captions: PropTypes.shape({
    breadcrumbs: PropTypes.arrayOf(PropTypes.string),
    title: PropTypes.string,
    description: PropTypes.string,
    submitButton: PropTypes.string,
    cancelButton: PropTypes.string,
    primaryFontTitle: PropTypes.string,
    secondaryFontTitle: PropTypes.string,
    noResultsText: PropTypes.string,
    primaryFontPopover: PropTypes.string,
    secondaryFontPopover: PropTypes.string,
  }).isRequired,
  fontsOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
    })
  ).isRequired,
  defaultOptions: PropTypes.shape({
    primaryFont: PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
    }),
    secondaryFont: PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
    }),
  }).isRequired,
  fontObserver: PropTypes.shape({
    observe: PropTypes.func,
  }).isRequired,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onPrimaryFontChange: PropTypes.func.isRequired,
  onSecondaryFontChange: PropTypes.func.isRequired,
  onDragEnd: PropTypes.func.isRequired,
};

export default AddFontPairDialog;
